import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth is delivered via Authorization: Bearer header (interceptor below).
// We intentionally do NOT use withCredentials — the Cloudflare ingress in this
// preview environment returns `Access-Control-Allow-Origin: *` which is
// incompatible with credentialed requests and triggers a browser "Network Error"
// before the request reaches our backend. Tokens are stored in localStorage and
// attached as Bearer; cookies are no longer required.
export const api = axios.create({ baseURL: API });

// Bearer-token fallback for browsers that strip third-party cookies (Safari ITP,
// Firefox strict, mobile webviews). We attach whichever token is appropriate
// for the request path so an admin opening an impersonation tab can keep BOTH
// tokens in localStorage without crossing wires.
//
// When admin impersonation is active (instabridge_attorney has impersonate_firm_id
// set), we ALSO inject X-Impersonate-Firm so the backend admin-direct-bypass
// path in auth_attorney.get_current_attorney accepts the admin token without
// needing a server-side attorney_session row.
api.interceptors.request.use((config) => {
  const adminToken = (() => { try { return JSON.parse(localStorage.getItem("instabridge_admin"))?.access_token; } catch { return null; } })();
  const attorneyBlob = (() => { try { return JSON.parse(localStorage.getItem("instabridge_attorney")); } catch { return null; } })();
  const attorneyToken = attorneyBlob?.session_token;
  const impFirmId = attorneyBlob?.impersonate_firm_id;
  const url = (config.url || "");
  const isPortal = url.startsWith("/portal") || url.startsWith("/attorney/auth");

  if (isPortal && impFirmId && adminToken) {
    // Admin direct-bypass path — most reliable; no attorney_session row needed.
    if (!config.headers.Authorization) config.headers.Authorization = `Bearer ${adminToken}`;
    if (!config.headers["X-Impersonate-Firm"]) config.headers["X-Impersonate-Firm"] = impFirmId;
    return config;
  }

  const token = isPortal ? (attorneyToken || adminToken) : (adminToken || attorneyToken);
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Convert FastAPI 422-style detail arrays into a render-safe string (React errors crash on objects).
export const formatApiError = (err) => {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => (d && typeof d.msg === "string" ? d.msg : JSON.stringify(d))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export const STATUS_LABELS = {
  new: "New",
  waiting_attorney: "Waiting on Attorney",
  under_review: "Under Review",
  approved: "Approved",
  funded: "Funded",
  declined: "Declined",
};

export const STATUS_ORDER = [
  "new",
  "waiting_attorney",
  "under_review",
  "approved",
  "funded",
  "declined",
];

export const formatCurrency = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

/**
 * Format a date for display. Auto-detects whether the input is a
 * calendar date (business date) versus a real timestamp with meaningful
 * time-of-day, and renders each correctly without dropping a day.
 *
 * The classic bug this fixes: `new Date("2026-07-31")` parses as UTC
 * midnight per ECMAScript. `toLocaleDateString("en-US")` then converts
 * to the browser's local timezone, so any US timezone (all west of UTC)
 * shifts back a day → renders "Jul 30". Same failure mode for any
 * ISO datetime whose time component is midnight UTC (e.g. a funding
 * date stored as `2026-06-26T00:00:00+00:00`).
 *
 * Rules:
 *   • `"YYYY-MM-DD"`                    → calendar date, format as-is
 *   • `"YYYY-MM-DDT00:00:00[.000][Z|±hh:mm]"` → calendar date (business
 *     date stored with a nominal midnight; rendering it via toLocale
 *     would silently shift). Treat as date-only.
 *   • Anything else                     → real timestamp, render via
 *     the browser locale (existing behavior).
 *
 * Never used on Mongo `created_at` / `updated_at` / `status_changed_at`
 * style timestamps because those always carry microsecond precision
 * from `datetime.now(timezone.utc)` and therefore never match the
 * midnight-shape guard above.
 */
const _DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const _MIDNIGHT_DT = /^(\d{4})-(\d{2})-(\d{2})T00:00:00(?:\.0+)?(?:Z|[+-]\d{2}:?\d{2})?$/;
const _MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export const formatDate = (input) => {
  if (!input) return "—";
  if (typeof input === "string") {
    const m = input.match(_DATE_ONLY) || input.match(_MIDNIGHT_DT);
    if (m) {
      // Format directly from string components — no Date() roundtrip,
      // no timezone shift possible.
      const [, y, mm, dd] = m;
      const monthIdx = parseInt(mm, 10) - 1;
      if (monthIdx >= 0 && monthIdx < 12) {
        return `${_MONTHS[monthIdx]} ${parseInt(dd, 10)}, ${y}`;
      }
    }
  }
  const d = new Date(input);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

/**
 * Fetch a protected PDF (or any binary) via the authenticated Axios client and
 * open it in a new tab. Required because <a href> / window.open(url) do NOT
 * fire the request interceptor — meaning the Bearer JWT *and* the
 * X-Impersonate-Firm header are silently stripped, producing a 401.
 *
 * `path` should be an `/api/...` path; we strip the leading /api so it works
 * with our axios baseURL.  Falls back to a download link if the popup is
 * blocked by the browser.
 */
export const openAuthedPdf = async (path, filename) => {
  // Allow callers to pass either "/api/portal/..." or "/portal/...".
  const rel = path.startsWith("/api/") ? path.slice(4) : path;
  const res = await api.get(rel, { responseType: "blob" });
  const blob = new Blob([res.data], { type: res.headers["content-type"] || "application/pdf" });
  const url = window.URL.createObjectURL(blob);
  const win = window.open(url, "_blank");
  if (!win) {
    // Popup blocked — trigger a same-tab download instead so the user still gets the file.
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "document.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  // Release memory after a generous delay so the new tab has finished loading.
  setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
};

export const formatRelative = (iso) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};
